import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { BadgeCheck } from 'lucide-react';
import { Button, Modal } from '@kioskgaming/ui';
import { useAuth } from '../hooks/useAuth';
import { confirmShopVerificationOtp, sendShopVerificationOtp } from '../services/api';
import { toShopProfile } from '../utils/sanitizeUser';

function safeNow() {
  return Date.now();
}

function secondsUntil(iso: string | null) {
  if (!iso) return 0;
  const ts = new Date(iso).getTime();
  if (!Number.isFinite(ts)) return 0;
  return Math.max(0, Math.ceil((ts - safeNow()) / 1000));
}

function onlyDigits6(value: string) {
  return value.replace(/\D/g, '').slice(0, 6);
}

export default function VerifyNowTrigger() {
  const { user, token, login } = useAuth();
  const verified = !!(user?.emailVerified || user?.phoneVerified);

  const [open, setOpen] = useState(false);
  const [channel, setChannel] = useState<'email' | 'phone'>(() => (user?.email ? 'email' : 'phone'));
  const [otp, setOtp] = useState('');
  const [sending, setSending] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [nextResendAt, setNextResendAt] = useState<string | null>(null);
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!open) return;
    const t = window.setInterval(() => setTick((x) => x + 1), 250);
    return () => window.clearInterval(t);
  }, [open]);

  useEffect(() => {
    if (open) return;
    setOtp('');
    setNextResendAt(null);
    setSending(false);
    setConfirming(false);
  }, [open]);

  useEffect(() => {
    if (user?.email) setChannel('email');
    else if (user?.phone) setChannel('phone');
  }, [user?.email, user?.phone]);

  const canEmail = !!user?.email;
  const canPhone = !!user?.phone;

  // `tick` exists to force re-render while counting down.
  const retrySeconds = secondsUntil(nextResendAt);
  const canRetry = retrySeconds <= 0;

  const send = useCallback(async (nextChannel: 'email' | 'phone') => {
    if (!user) return;
    setChannel(nextChannel);
    setSending(true);
    try {
      const res = await sendShopVerificationOtp({ channel: nextChannel });
      if (!res.success) {
        toast.error(res.message || 'Failed to send OTP');
        return;
      }
      setNextResendAt(res.data?.next_resend_at ?? null);
      toast.success(`OTP sent via ${nextChannel}`);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to send OTP');
    } finally {
      setSending(false);
    }
  }, [user]);

  const confirm = useCallback(async () => {
    if (!user) return;
    const cleaned = onlyDigits6(otp);
    if (cleaned.length !== 6) {
      toast.error('Please enter the 6-digit OTP');
      return;
    }
    setConfirming(true);
    try {
      const res = await confirmShopVerificationOtp({ channel, otp: cleaned });
      if (!res.success) {
        toast.error(res.message || 'Verification failed');
        return;
      }
      const updatedShop = res.data?.shop;
      if (updatedShop && token) {
        login(toShopProfile(updatedShop as Record<string, unknown>), token);
      }
      toast.success('Verified successfully');
      setOpen(false);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Verification failed');
    } finally {
      setConfirming(false);
    }
  }, [channel, otp, user, token, login]);

  if (!user || verified) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex h-10 items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 text-sm font-semibold text-emerald-800 shadow-sm transition hover:bg-emerald-100"
        aria-label="Verify account"
      >
        <BadgeCheck className="h-[18px] w-[18px]" strokeWidth={2} aria-hidden />
        Verify now
      </button>

      <Modal
        isOpen={open}
        onClose={() => setOpen(false)}
        title="Verify your Shop account"
        subtitle="Enter the OTP we send to your email or phone. Email is recommended."
        variant="form"
        size="sm"
        primaryAction={{
          label: 'Verify',
          onClick: () => void confirm(),
          loading: confirming,
          disabled: sending || confirming,
        }}
        secondaryAction={{
          label: 'Close',
          onClick: () => setOpen(false),
        }}
      >
        <div className="space-y-4">
          <div className="rounded-xl border border-stone-200 bg-white p-3 text-xs text-stone-700">
            <div className="font-semibold text-stone-900">Status</div>
            <div className="mt-1">
              Email: <span className="font-medium">{canEmail ? user.email : '—'}</span>
            </div>
            <div className="mt-0.5">
              Phone: <span className="font-medium">{canPhone ? user.phone : '—'}</span>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant={channel === 'email' ? 'primary' : 'outline'}
              onClick={() => void send('email')}
              disabled={!canEmail || sending || (!canRetry && channel === 'email')}
              loading={sending && channel === 'email'}
            >
              {canRetry || channel !== 'email' ? 'Send OTP to email' : `Retry in ${retrySeconds}s`}
            </Button>
            <Button
              type="button"
              variant={channel === 'phone' ? 'primary' : 'outline'}
              onClick={() => void send('phone')}
              disabled={!canPhone || sending || (!canRetry && channel === 'phone')}
              loading={sending && channel === 'phone'}
            >
              {canRetry || channel !== 'phone' ? 'Send OTP to phone' : `Retry in ${retrySeconds}s`}
            </Button>
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-stone-900">OTP</label>
            <input
              value={otp}
              onChange={(e) => setOtp(onlyDigits6(e.target.value))}
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="6-digit code"
              className="h-11 w-full rounded-xl border border-stone-200 bg-white px-3 text-sm font-semibold tracking-[0.3em] text-stone-900 outline-none focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100"
            />
          </div>
        </div>
      </Modal>
    </>
  );
}

